import React, { ComponentProps, ReactElement, useCallback } from "react";

import { useActions, useAppSelector } from "cortex/ui/src/hooks";
import {
  currentDeviceUnloadAction,
  fetchDeviceAction,
} from "cortex/ui/src/redux-store/ui-state/current-device/actions";
import { currentDeviceSelector } from "cortex/ui/src/redux-store/ui-state/current-device/selectors";
import { storesReady } from "cortex/ui/src/redux-store/utils";

import { FetchController } from "../fetch-controller";

type FetchControllerProps = ComponentProps<typeof FetchController>;

interface FetchDeviceControllerProps
  extends Omit<
    FetchControllerProps,
    | "fetchObject"
    | "injectAs"
    | "objectId"
    | "object"
    | "objectLoaded"
    | "unloadObject"
  > {
  children?: ReactElement;
  deviceId: string;
}

function FetchDeviceController({
  children,
  deviceId,
  ...restProps
}: FetchDeviceControllerProps) {
  const object = useAppSelector((state: any) => currentDeviceSelector(state));

  const objectLoaded = useAppSelector((state: any) => {
    return storesReady(state.pages.currentDevice);
  });

  const { fetchDevice, unloadCurrentDevice } = useActions({
    fetchDevice: fetchDeviceAction,
    unloadCurrentDevice: () => currentDeviceUnloadAction,
  });

  const fetchObject = useCallback(
    (deviceIdValue: string, params?: unknown, options?: unknown) => {
      return new Promise((resolve, reject) => {
        fetchDevice({
          reject,
          resolve,
          serviceArgs: {
            deviceId: deviceIdValue,
            options,
            params,
          },
        });
      });
    },
    [fetchDevice],
  );

  const unloadObject = useCallback(() => {
    unloadCurrentDevice();
  }, [unloadCurrentDevice]);

  return (
    <FetchController
      {...restProps}
      fetchObject={fetchObject}
      injectAs="device"
      object={object}
      objectId={deviceId}
      objectLoaded={objectLoaded}
      unloadObject={unloadObject}
    >
      {children}
    </FetchController>
  );
}

export { FetchDeviceController };
export default FetchDeviceController;
